import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { initials, formatDuration } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Sparkle, TrendUp, Cpu, Warning, LightbulbFilament, ArrowsClockwise, Ticket as TicketIcon, CheckCircle, Trophy, Globe, MapPin, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = { new: "#A78BFA", waiting: "#60A5FA", in_progress: "#FBBF24", resolved: "#39A36E" };
const STATUS_LABELS = { new: "New", waiting: "Waiting on Requester", in_progress: "In Progress", resolved: "Resolved" };

const ISSUE_LABEL = {
  login_error: "Login errors",
  crash: "Crashes",
  slow_performance: "Slow / performance",
  connectivity: "Connectivity",
  access_denied: "Access denied",
  hardware_failure: "Hardware",
  configuration: "Configuration",
  feature_request: "Feature request",
  data_loss: "Data loss",
  other: "Other",
};

function labelIssue(k) { return ISSUE_LABEL[k] || k; }

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function fmtSecs(secs) {
  if (secs == null) return "—";
  const h = secs / 3600;
  if (h >= 1) return `${h.toFixed(1)}h`;
  return `${Math.round(secs / 60)}m`;
}

export default function Insights() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardFilters, setLeaderboardFilters] = useState({
    region: "all",
    from_date: isoDaysAgo(30),
    to_date: isoDaysAgo(0),
  });

  const loadLeaderboard = (f = leaderboardFilters) => {
    const q = new URLSearchParams({
      region: f.region, from_date: f.from_date, to_date: f.to_date,
    }).toString();
    api.get(`/tickets/team/resolved-leaderboard?${q}`).then((r) => setLeaderboard(r.data)).catch(() => {});
  };

  const load = () => api.get("/analytics/insights").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    loadLeaderboard();
  }, []);

  const runTrends = async () => {
    setLoadingTrends(true);
    setTrends(null);
    try {
      const r = await api.post("/analytics/ai-trends?limit=50");
      setTrends(r.data);
      toast.success(`Analyzed ${r.data.tickets_analyzed} tickets`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI trends failed");
    } finally { setLoadingTrends(false); }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const r = await api.post("/analytics/backfill");
      toast.success(`Classified ${r.data.updated} of ${r.data.scanned} tickets`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;

  const systemChart = data.by_system.map((s) => ({ name: s.name, key: s.name, count: s.count }));
  const issueChart = data.by_issue_type.map((s) => ({ name: labelIssue(s.name), key: s.name, count: s.count }));
  const systems = Object.keys(data.matrix);
  const allIssues = Array.from(new Set(systems.flatMap((s) => Object.keys(data.matrix[s]))));
  const maxCell = Math.max(1, ...systems.flatMap((s) => Object.values(data.matrix[s])));

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Insights</h1>
          <p className="text-sm text-slate-500 mt-1">Which systems break most? What are your users struggling with?</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 font-mono">
            {data.total} total ·{" "}
            <button
              onClick={() => navigate("/tickets?system=__none__")}
              className="underline underline-offset-2 hover:text-[#4f4c90]"
              data-testid="insights-unclassified-link"
              title="View tickets AI hasn't classified yet"
            >{data.unclassified} unclassified</button>
          </span>
          {user?.role === "admin" && data.unclassified > 0 && (
            <Button onClick={backfill} disabled={backfilling} variant="outline" size="sm"
              data-testid="insights-backfill-button"
              className="border-slate-200 bg-slate-100 h-8 gap-1">
              <ArrowsClockwise size={12} className={backfilling ? "animate-spin" : ""} />
              {backfilling ? "Classifying…" : "AI-classify old tickets"}
            </Button>
          )}
        </div>
      </div>

      <TeamStatsRow stats={stats} />
      <TicketTrendsRow stats={stats} navigate={navigate} />
      <PriorityBreakdown stats={stats} />
      <RegionBreakdown stats={stats} />
      <TeamResolvedLeaderboard
        data={leaderboard}
        currentUserId={user?.id}
        filters={leaderboardFilters}
        onFiltersChange={(f) => { setLeaderboardFilters(f); loadLeaderboard(f); }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="hairline rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Cpu size={16} weight="duotone" className="text-[#4f4c90]" />
            <div className="font-heading font-semibold">Tickets by system</div>
          </div>
          <div className="h-64" data-testid="insights-by-system-chart">
            <ResponsiveContainer>
              <BarChart data={systemChart} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#64748b" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis dataKey="name" type="category" stroke="#64748b" tick={{ fontSize: 11, fill: "#334155" }} width={110} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", fontSize: 12, color: "#0f172a" }} cursor={{ fill: "rgba(79,76,144,0.06)" }} />
                <Bar
                  dataKey="count"
                  fill="#4f4c90"
                  radius={[0, 3, 3, 0]}
                  onClick={(d) => d?.key && navigate(`/tickets?system=${encodeURIComponent(d.key)}`)}
                  style={{ cursor: "pointer" }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-slate-500 text-right">Click a bar to view those tickets →</div>
        </div>

        <div className="hairline rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Warning size={16} weight="duotone" className="text-[#FB923C]" />
            <div className="font-heading font-semibold">Tickets by issue type</div>
          </div>
          <div className="h-64" data-testid="insights-by-issue-chart">
            <ResponsiveContainer>
              <BarChart data={issueChart} layout="vertical" margin={{ left: 40 }}>
                <XAxis type="number" stroke="#64748b" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis dataKey="name" type="category" stroke="#64748b" tick={{ fontSize: 11, fill: "#334155" }} width={140} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", fontSize: 12, color: "#0f172a" }} cursor={{ fill: "rgba(251,146,60,0.08)" }} />
                <Bar
                  dataKey="count"
                  fill="#FB923C"
                  radius={[0, 3, 3, 0]}
                  onClick={(d) => d?.key && navigate(`/tickets?issue_type=${encodeURIComponent(d.key)}`)}
                  style={{ cursor: "pointer" }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-slate-500 text-right">Click a bar to view those tickets →</div>
        </div>
      </div>

      <div className="hairline rounded-lg p-4 bg-white overflow-x-auto">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <div className="font-heading font-semibold">System × Issue-type heatmap</div>
          <div className="text-[11px] text-slate-500">Click any cell or system to view its tickets →</div>
        </div>
        {systems.length === 0 ? (
          <div className="text-slate-500 text-sm py-6">Not enough classified tickets yet.</div>
        ) : (
          <table className="w-full text-xs" data-testid="insights-heatmap">
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase text-slate-500 font-medium tracking-wider p-2"></th>
                {allIssues.map((i) => (
                  <th
                    key={i}
                    onClick={() => navigate(`/tickets?issue_type=${encodeURIComponent(i)}`)}
                    className="text-[10px] uppercase text-slate-500 font-medium tracking-wider p-2 min-w-[80px] cursor-pointer hover:text-[#4f4c90]"
                    title={`View all ${labelIssue(i)} tickets`}
                  >
                    {labelIssue(i)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {systems.map((s) => (
                <tr key={s} className="border-t border-slate-100">
                  <td
                    onClick={() => navigate(`/tickets?system=${encodeURIComponent(s)}`)}
                    className="p-2 text-slate-800 font-medium whitespace-nowrap cursor-pointer hover:text-[#4f4c90]"
                    title={`View all ${s} tickets`}
                  >
                    {s}
                  </td>
                  {allIssues.map((i) => {
                    const v = data.matrix[s]?.[i] || 0;
                    const opacity = v === 0 ? 0 : 0.15 + (v / maxCell) * 0.65;
                    const cellClickable = v > 0;
                    return (
                      <td key={i} className="p-2 text-center">
                        <div
                          onClick={() => cellClickable && navigate(`/tickets?system=${encodeURIComponent(s)}&issue_type=${encodeURIComponent(i)}`)}
                          data-testid={`heatmap-cell-${s}-${i}`}
                          className={`mx-auto rounded font-mono text-[11px] w-10 h-7 flex items-center justify-center ${cellClickable ? "cursor-pointer hover:ring-2 hover:ring-[#4f4c90]/40 transition" : ""}`}
                          style={{
                            background: v ? `rgba(79, 76, 144, ${opacity})` : "transparent",
                            color: v ? (opacity > 0.4 ? "#fff" : "#4f4c90") : "#cbd5e1"
                          }}
                          title={cellClickable ? `${v} ${s} ticket${v === 1 ? "" : "s"} tagged '${labelIssue(i)}'` : undefined}
                        >
                          {v || "·"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="hairline rounded-lg p-5 bg-white border-[#4f4c90]/20">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#8B87C8]" />
              <div className="font-heading font-semibold">AI trends & root causes</div>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Claude reads the latest 50 tickets and looks for recurring themes so you can fix the source, not the symptoms.
            </p>
          </div>
          <Button onClick={runTrends} disabled={loadingTrends}
            data-testid="insights-ai-trends-button"
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1">
            <TrendUp size={14} weight="bold" />
            {loadingTrends ? "Analyzing…" : trends ? "Re-analyze" : "Analyze last 50 tickets"}
          </Button>
        </div>

        {trends && (
          <div className="mt-5 space-y-4 fade-in" data-testid="insights-ai-trends-result">
            <div>
              <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-1">Summary</div>
              <div className="text-sm text-slate-800">{trends.summary}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-2 flex items-center gap-1">
                  <TrendUp size={12} /> Recurring themes
                </div>
                <ul className="space-y-1.5 text-sm text-slate-800">
                  {trends.recurring_themes.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#8B87C8]">·</span>{t}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-2 flex items-center gap-1">
                  <Warning size={12} /> Likely root causes
                </div>
                <ul className="space-y-1.5 text-sm text-slate-800">
                  {trends.root_causes.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#FB923C]">·</span>{t}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium mb-2 flex items-center gap-1">
                  <LightbulbFilament size={12} /> Recommendations
                </div>
                <ul className="space-y-1.5 text-sm text-slate-800">
                  {trends.recommendations.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#39A36E]">·</span>{t}</li>)}
                </ul>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 font-mono border-t border-slate-200 pt-2">
              Based on {trends.tickets_analyzed} most recent tickets
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamStatsRow({ stats }) {
  if (!stats) return null;
  const s = stats;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insights-team-stats">
      <StatCard label="Total tickets" value={s.total_tickets} icon={TicketIcon} color="#4f4c90" to="/tickets" />
      <StatCard
        label="Avg resolution"
        value={`${s.avg_resolution_hours}h`}
        sub={s.shared_resolved_count ? `all teams · ${s.shared_resolved_count} shared excluded from per-team` : "all teams"}
        icon={CheckCircle} color="#39A36E"
        to="/tickets?status=resolved"
      />
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color, testId, to }) {
  const inner = (
    <div className="hairline rounded-lg p-4 bg-white transition hover:border-[#4f4c90]/40 hover:shadow-sm h-full" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</div>
        <Icon size={18} weight="duotone" color={color} />
      </div>
      <div className="mt-2 font-heading text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

function TicketTrendsRow({ stats, navigate }) {
  if (!stats) return null;
  const statusData = Object.entries(stats.by_status).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, key: k, value: v, fill: STATUS_COLORS[k],
  }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="hairline rounded-lg p-4 bg-white lg:col-span-2">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="font-heading font-semibold">Tickets — last 7 days</div>
            <div className="text-xs text-slate-500">Created vs resolved</div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={stats.trend_7d} margin={{ top: 6, right: 8, left: -8, bottom: 4 }}>
              <XAxis dataKey="day" stroke="#64748b" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis stroke="#64748b" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, color: "#0f172a" }} cursor={{ fill: "rgba(79,76,144,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#64748b" }} />
              <Bar dataKey="created" name="Created" fill="#4f4c90" radius={[3,3,0,0]} />
              <Bar dataKey="resolved" name="Resolved" fill="#39A36E" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="hairline rounded-lg p-4 bg-white">
        <div className="font-heading font-semibold">By status</div>
        <div className="text-xs text-slate-500 mb-2">Click a slice to see those tickets</div>
        <div className="h-56">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={statusData}
                innerRadius={45}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
                onClick={(d) => d?.key && navigate(`/tickets?status=${d.key}`)}
                className="cursor-pointer"
              >
                {statusData.map((d, i) => <Cell key={i} fill={d.fill} style={{ cursor: "pointer" }} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, color: "#0f172a" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#64748b" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PriorityBreakdown({ stats }) {
  if (!stats) return null;
  const prioData = Object.entries(stats.by_priority).map(([k, v]) => ({ name: k, value: v }));
  return (
    <div className="hairline rounded-lg p-4 bg-white">
      <div className="font-heading font-semibold mb-1">Priority breakdown</div>
      <div className="text-xs text-slate-500 mb-3">Click a priority to view its tickets</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {prioData.map((p) => (
          <Link
            key={p.name}
            to={`/tickets?priority=${p.name}`}
            className={`chip prio-${p.name} !text-sm !py-2 !px-3 justify-between hover:brightness-95 transition`}
            data-testid={`insights-priority-${p.name}`}
          >
            <span className="capitalize">{p.name}</span>
            <span className="font-mono">{p.value}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RegionBreakdown({ stats }) {
  if (!stats) return null;
  const byRegion = stats.by_region || {};
  const byRegionLocation = stats.by_region_location || {};
  const unregioned = stats.unregioned || 0;
  const regionKeys = Object.keys(byRegion);
  const hasAny = regionKeys.length > 0 || unregioned > 0;
  if (!hasAny) return null;

  const totalTagged = regionKeys.reduce((n, k) => n + byRegion[k], 0);
  const sortedRegions = regionKeys.sort((a, b) => byRegion[b] - byRegion[a]);

  return (
    <div className="hairline rounded-lg p-4 bg-white" data-testid="insights-region-breakdown">
      <div className="flex items-center justify-between mb-1">
        <div className="font-heading font-semibold flex items-center gap-2">
          <Globe size={16} weight="duotone" className="text-[#4f4c90]" />
          Where tickets are coming from
        </div>
        <Link to="/tickets" className="text-xs text-[#8B87C8] hover:text-slate-900">View all →</Link>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        {totalTagged} ticket{totalTagged === 1 ? "" : "s"} tagged with a region
        {unregioned > 0 && <span> · {unregioned} untagged</span>}
      </div>
      {regionKeys.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3">
          No tickets have been tagged with a region yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedRegions.map((region) => {
            const count = byRegion[region];
            const pct = totalTagged > 0 ? Math.round((count / totalTagged) * 100) : 0;
            const locations = byRegionLocation[region] || {};
            const sortedLocs = Object.entries(locations).sort((a, b) => b[1] - a[1]);
            return (
              <div key={region} className="rounded-md border border-slate-200 bg-slate-50/60 p-3 hover:border-[#4f4c90]/40 transition" data-testid={`region-card-${region}`}>
                <Link to={`/tickets?region=${encodeURIComponent(region)}`} className="flex items-center justify-between hover:opacity-80">
                  <div className="flex items-center gap-2">
                    <span className="chip !text-[#8B87C8] !border-[#4f4c90]/30 bg-[#4f4c90]/10 !text-xs !py-0.5">{region}</span>
                  </div>
                  <div className="font-heading font-bold text-lg text-slate-800">{count}</div>
                </Link>
                <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-[#4f4c90] transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-1">{pct}% of tagged</div>
                {sortedLocs.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {sortedLocs.slice(0, 5).map(([loc, n]) => {
                      const isUnspec = loc === "(unspecified)";
                      const href = isUnspec
                        ? `/tickets?region=${encodeURIComponent(region)}&location=__none__`
                        : `/tickets?region=${encodeURIComponent(region)}&location=${encodeURIComponent(loc)}`;
                      return (
                        <li key={loc}>
                          <Link to={href} className="flex items-center justify-between text-xs text-slate-700 rounded px-1 -mx-1 py-0.5 hover:bg-white hover:text-[#4f4c90]">
                            <span className="flex items-center gap-1 truncate">
                              <MapPin size={10} weight="duotone" className="text-slate-400 flex-shrink-0" />
                              <span className={isUnspec ? "italic text-slate-500" : ""}>{loc}</span>
                            </span>
                            <span className="font-mono text-slate-600">{n}</span>
                          </Link>
                        </li>
                      );
                    })}
                    {sortedLocs.length > 5 && (
                      <li className="text-[11px] text-slate-500 font-mono">+{sortedLocs.length - 5} more…</li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
          {unregioned > 0 && (
            <Link to="/tickets?region=__none__" className="rounded-md border border-dashed border-slate-200 bg-slate-50/40 p-3 hover:border-[#4f4c90]/40 hover:bg-slate-50 transition block">
              <div className="flex items-center justify-between">
                <span className="chip !text-slate-500 !border-slate-300 bg-slate-100 !text-xs !py-0.5">Untagged</span>
                <div className="font-heading font-bold text-lg text-slate-500">{unregioned}</div>
              </div>
              <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                Tickets AI could not confidently map to a region. Click to review and assign.
              </div>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function TeamResolvedLeaderboard({ data, currentUserId, filters, onFiltersChange }) {
  const settings = useSettings();
  const [expanded, setExpanded] = useState({}); // keyed by user_id or "_bucket"
  if (!data) return null;
  const entries = data.entries || [];
  const bucket = data.no_region_bucket || { count: 0, tickets: [] };
  const drilldown = data.drilldown || {};
  const regionOptions = ["all", ...(settings?.regions?.map((r) => r.name) || []), "none"];
  const weekTop = Math.max(1, ...entries.map((e) => e.resolved_this_week || 0));
  const totalTop = Math.max(1, ...entries.map((e) => e.resolved_total || 0));
  const teamWeek = entries.reduce((n, e) => n + (e.resolved_this_week || 0), 0);
  const teamTotal = entries.reduce((n, e) => n + (e.resolved_total || 0), 0);
  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));
  const setFilter = (patch) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className="hairline rounded-lg p-4 bg-white" data-testid="team-resolved-leaderboard">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold flex items-center gap-2">
            <Trophy size={16} weight="duotone" className="text-[#4f4c90]" />
            Tickets done per person
          </div>
          <div className="text-xs text-slate-500">
            {teamWeek} this week · {teamTotal} in window across {entries.length} agent{entries.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Region</Label>
            <Select value={filters.region} onValueChange={(v) => setFilter({ region: v })}>
              <SelectTrigger data-testid="leaderboard-region" className="bg-white border-slate-200 h-9 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {regionOptions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r === "all" ? "All regions" : r === "none" ? "No region set" : r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
            <Input
              type="date" data-testid="leaderboard-from"
              value={filters.from_date}
              onChange={(e) => setFilter({ from_date: e.target.value })}
              className="bg-white border-slate-200 h-9 w-36"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
            <Input
              type="date" data-testid="leaderboard-to"
              value={filters.to_date}
              onChange={(e) => setFilter({ to_date: e.target.value })}
              className="bg-white border-slate-200 h-9 w-36"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="w-6"></th>
              <th className="text-left font-medium py-2 pr-3">Agent</th>
              <th className="text-left font-medium py-2 px-3">This week</th>
              <th className="text-left font-medium py-2 px-3">In window</th>
              <th className="text-left font-medium py-2 pl-3">Avg resolution</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bucket.count > 0 && (
              <>
                <tr
                  className="cursor-pointer hover:bg-amber-50/50"
                  onClick={() => toggle("_bucket")}
                  data-testid="leaderboard-no-region-row"
                >
                  <td className="py-2 pl-1">
                    {expanded._bucket ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  </td>
                  <td className="py-2 pr-3" colSpan={3}>
                    <div className="flex items-center gap-2">
                      <span className="chip !text-amber-700 !border-amber-200 bg-amber-50 !text-xs">No region set</span>
                      <span className="text-xs text-slate-500">{bucket.count} ticket{bucket.count === 1 ? "" : "s"} — set a region on each to clock them</span>
                    </div>
                  </td>
                  <td className="py-2 pl-3 whitespace-nowrap">
                    <span className="font-mono text-sm text-slate-400">not timed</span>
                  </td>
                </tr>
                {expanded._bucket && (
                  <tr><td></td><td colSpan={4} className="p-0">
                    <DrilldownList tickets={bucket.tickets} showResolver />
                  </td></tr>
                )}
              </>
            )}
            {entries.map((e) => {
              const isMe = currentUserId && e.user_id === currentUserId;
              const weekPct = Math.round(((e.resolved_this_week || 0) / weekTop) * 100);
              const totalPct = Math.round(((e.resolved_total || 0) / totalTop) * 100);
              const open = expanded[e.user_id];
              const tickets = drilldown[e.user_id] || [];
              return (
                <>
                  <tr
                    key={e.user_id || e.name}
                    className={`cursor-pointer ${isMe ? "bg-[#4f4c90]/5" : ""} hover:bg-slate-50`}
                    onClick={() => toggle(e.user_id)}
                    data-testid={`leaderboard-row-${e.user_id}`}
                  >
                    <td className="py-2 pl-1">{open ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-[#4f4c90]/15 border border-[#4f4c90]/30 flex items-center justify-center text-[10px] font-medium text-[#4f4c90] flex-shrink-0">
                          {initials(e.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            <span className="truncate">{e.name}</span>
                            {isMe && <span className="text-[10px] uppercase tracking-wider font-mono text-[#4f4c90]">you</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 w-1/3">
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-sm w-6 tabular-nums">{e.resolved_this_week}</div>
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-[#39A36E]" style={{ width: `${weekPct}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 w-1/3">
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-sm w-8 tabular-nums">{e.resolved_total}</div>
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-[#4f4c90]" style={{ width: `${totalPct}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pl-3 whitespace-nowrap">
                      <span className="font-mono text-sm tabular-nums text-slate-700">
                        {e.avg_resolution_hours == null
                          ? <span className="text-slate-400">—</span>
                          : e.avg_resolution_hours >= 1
                            ? `${e.avg_resolution_hours}h`
                            : `${Math.round(e.avg_resolution_hours * 60)}m`}
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr><td></td><td colSpan={4} className="p-0">
                      <DrilldownList tickets={tickets} />
                    </td></tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DrilldownList({ tickets, showResolver = false }) {
  const regioned = tickets.filter((t) => t.region);
  const unregioned = tickets.filter((t) => !t.region);
  if (tickets.length === 0) {
    return <div className="px-4 py-3 text-[11px] text-slate-500">No tickets in this slice.</div>;
  }
  return (
    <div className="bg-slate-50/60 border-l-2 border-slate-200 ml-6 my-1 rounded">
      <table className="w-full text-xs" data-testid="leaderboard-drilldown">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500">
            <th className="text-left font-medium py-1.5 px-3">Ticket</th>
            <th className="text-left font-medium py-1.5 px-3">Subject</th>
            <th className="text-left font-medium py-1.5 px-3">Region</th>
            <th className="text-left font-medium py-1.5 px-3">Opened</th>
            <th className="text-left font-medium py-1.5 px-3">Resolved</th>
            <th className="text-left font-medium py-1.5 px-3">BH time</th>
          </tr>
        </thead>
        <tbody>
          {regioned.map((t) => (
            <tr key={t.id} className="hover:bg-white">
              <td className="py-1.5 px-3 font-mono text-[11px]">
                <Link to={`/tickets/${t.id}`} className="hover:text-[#4f4c90]">#{t.number}</Link>
              </td>
              <td className="py-1.5 px-3 truncate max-w-xs">{t.subject}</td>
              <td className="py-1.5 px-3">
                <span className="chip !text-slate-600 !border-slate-200 bg-slate-100 !text-[10px]">{t.region}</span>
                {t.using_workspace_fallback && (
                  <span className="chip !text-amber-700 !border-amber-200 bg-amber-50 !text-[10px] ml-1" title="Using workspace calendar fallback — set the region's own hours in Settings.">
                    ⚠ fallback
                  </span>
                )}
              </td>
              <td className="py-1.5 px-3 font-mono text-[10px] text-slate-500">
                {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
              </td>
              <td className="py-1.5 px-3 font-mono text-[10px] text-slate-500">
                {t.resolved_at ? new Date(t.resolved_at).toLocaleString() : "—"}
              </td>
              <td className="py-1.5 px-3 font-mono tabular-nums">{fmtSecs(t.business_hours_seconds)}</td>
            </tr>
          ))}
          {unregioned.length > 0 && (
            <>
              <tr><td colSpan={6} className="pt-2 pb-1 px-3 text-[10px] uppercase tracking-wider text-amber-700">
                No region set — not timed
              </td></tr>
              {unregioned.map((t) => (
                <tr key={t.id} className="hover:bg-white opacity-80">
                  <td className="py-1.5 px-3 font-mono text-[11px]">
                    <Link to={`/tickets/${t.id}`} className="hover:text-[#4f4c90]">#{t.number}</Link>
                  </td>
                  <td className="py-1.5 px-3 truncate max-w-xs">{t.subject}</td>
                  <td className="py-1.5 px-3 text-slate-400 italic">—</td>
                  <td className="py-1.5 px-3 font-mono text-[10px] text-slate-500">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-1.5 px-3 font-mono text-[10px] text-slate-500">
                    {t.resolved_at ? new Date(t.resolved_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-1.5 px-3 text-slate-400">not timed</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}


